import type { Clock } from '@/lib/clock';

import type { Session, SessionEnding } from './session';
import type { SessionStorage } from './session-storage';

/**
 * The one thing that holds the session: the token, its `expiresAt` and the
 * signed-in user, together, in a single object with a single way in and a
 * single way out.
 *
 * Deliberately not a React hook. It is created once at the composition root and
 * handed to two consumers that must agree — the API client, which reads the
 * token for every back-office call, and `SessionProvider`, which renders it.
 * Made with `useState` instead, the client would have to reach into a render
 * for a credential, and the two would drift the first time one of them
 * re-rendered and the other did not.
 *
 * **There is no timer here, and that is the design.** A session ends twelve
 * hours after it was created or thirty minutes after its last request,
 * whichever comes first, and the server checks both on every single request. Its
 * answer is the truth. A client-side countdown would be a second opinion drawn
 * from a clock the user can change — at best a courtesy that avoids one doomed
 * request, at worst a screen that insists you are signed in for twenty minutes
 * after the API stopped agreeing. The one thing that ends a session here is
 * being told: `AUTH_SESSION_EXPIRED` from any request, or the user signing out.
 */
/**
 * Every member is a property holding a function rather than a method, so that
 * pulling one off — `useSyncExternalStore(holder.subscribe, holder.read)` — is
 * safe by type and not merely by the implementation happening to use closures.
 */
export interface SessionHolder {
  /** The session, or `null`. A stable reference until it changes. */
  readonly read: () => Session | null;

  /**
   * The bearer token, or `undefined` when nobody is signed in.
   *
   * This is what the API client's `session` credential provider calls, once per
   * request. `undefined` sends no credential at all rather than an empty one,
   * and lets the API answer its own 401.
   */
  readonly token: () => string | undefined;

  /** Notified whenever the session appears or disappears. */
  readonly subscribe: (listener: () => void) => () => void;

  /** Take a completed sign-in. The only way a session is established. */
  readonly signedIn: (session: Session) => void;

  /**
   * Replace the signed-in user with what the server has just said about them,
   * keeping the same token and the same `expiresAt`.
   *
   * It exists for one caller — the credential gate (FE007) — and the argument is
   * deliberately the *whole* user the API answered with rather than a flag. The
   * flag it carries is `mustChangeCredential`, and the only correct source for
   * it after a change is the response: a PIN-only change may legitimately leave
   * it set, so "the request returned 200" and "the gate is clear" are different
   * statements. There is no way to spell the second one locally here, on
   * purpose. An earlier server-side bug cleared that flag on a PIN-only change
   * and left a leaked provisioned password live; nothing in this client should
   * be able to re-create the same shape from the other side.
   *
   * It does not establish a session. If there is none — signed out, or the API
   * ended it while the request was in flight — this does nothing rather than
   * assembling a user with no token, which would render as signed in and be
   * refused by everything.
   */
  readonly credentialChanged: (user: Session['user']) => void;

  /**
   * End it, here, now, and without asking anybody.
   *
   * Called for both endings — the API answering `AUTH_SESSION_EXPIRED`, and a
   * sign-out — because they are the same locally: the token is gone from this
   * browser and the next request carries none. Idempotent, because both can
   * happen to the same session (a sign-out whose request is refused as expired
   * arrives here twice).
   *
   * The reason is required rather than optional so that a future third caller
   * has to say which ending it is instead of defaulting into one of these two
   * and quietly mis-wording the sign-in screen.
   */
  readonly clear: (reason: SessionEnding) => void;

  /**
   * How the last session ended in this tab, or `null` if none has.
   *
   * In memory only — never written to storage. See `SessionEnding`: a reload is
   * not an ending, and this must not outlive one.
   */
  readonly endedBecause: () => SessionEnding | null;

  /**
   * Forget the recorded ending, because it has been shown.
   *
   * Separate from `signedIn` clearing it: somebody can be told why they were
   * thrown out and then walk away without signing back in, and the next arrival
   * at that screen is a new trip that the old reason no longer describes.
   *
   * It does not notify. Nothing observes this through the `read` snapshot — the
   * session is already `null` and stays `null` — so a notify would be a render
   * nobody asked for. `SessionProvider` mirrors the value into React state and
   * that is what re-renders.
   */
  readonly acknowledgeEnding: () => void;
}

/**
 * Whether a restored session is already past its absolute ceiling.
 *
 * A courtesy and not enforcement — the distinction matters. The server decides;
 * this only avoids restoring a token that provably cannot work, so a reload
 * after lunch lands on sign-in instead of flashing a shell that 401s on its
 * first fetch. It cannot see the thirty-minute idle window at all, which is
 * server state, so a session inside its ceiling but idle past its window is
 * restored here and ended by the first request. That is the correct outcome and
 * the reason no larger client-side judgement is attempted.
 *
 * `Date.parse` reads no clock; `now` is the injected one.
 */
function isPastCeiling(session: Session, now: number): boolean {
  const expiresAt = Date.parse(session.expiresAt);

  // A timestamp that will not parse is not a session anybody should be trusted
  // with. Discarding costs one sign-in; the alternative is `NaN >= now` being
  // false and a broken record being treated as live.
  if (Number.isNaN(expiresAt)) return true;

  return now >= expiresAt;
}

export interface SessionHolderOptions {
  storage: SessionStorage;
  clock: Clock;
}

export function createSessionHolder({ storage, clock }: SessionHolderOptions): SessionHolder {
  const listeners = new Set<() => void>();

  const notify = () => {
    for (const listener of listeners) listener();
  };

  /**
   * A reload restores the session from storage rather than dropping it, which
   * is the behaviour `session-storage.ts` argues for at length. What is
   * restored is believed only until the next request answers.
   */
  const restore = (): Session | null => {
    const stored = storage.read();

    if (stored === null) return null;

    if (isPastCeiling(stored, clock())) {
      storage.clear();

      return null;
    }

    return stored;
  };

  let current = restore();

  /**
   * Deliberately a plain variable and not part of the `useSyncExternalStore`
   * snapshot. Every transition that sets it also changes `current`, so the
   * notify that follows is what re-renders; there is nothing here that can
   * change on its own and be missed.
   */
  let ending: SessionEnding | null = null;

  return {
    read: () => current,

    endedBecause: () => ending,

    acknowledgeEnding: () => {
      ending = null;
    },

    token: () => current?.token,

    subscribe: (listener) => {
      listeners.add(listener);

      return () => {
        listeners.delete(listener);
      };
    },

    signedIn: (session) => {
      current = session;
      // A completed sign-in answers whatever the last ending was asking about,
      // so the reason goes with it. Left set, somebody who signed out, signed
      // straight back in and then hit a lapse would be told the wrong story.
      ending = null;
      storage.write(session);
      notify();
    },

    credentialChanged: (user) => {
      // Nothing to update, and nothing to invent: without a session there is no
      // token this user belongs to. A response landing after a sign-out — or
      // after `AUTH_SESSION_EXPIRED` cleared this — is dropped here rather than
      // resurrecting a tokenless session that looks signed in.
      if (current === null) return;

      // Persisted as well as held, so a reload does not restore the pre-change
      // user and send somebody back to a gate they have already passed.
      current = { ...current, user };
      storage.write(current);
      notify();
    },

    clear: (reason) => {
      // Storage is cleared whether or not anything was in memory: the two can
      // disagree — a duplicated tab, a write that landed and a page that did
      // not finish loading — and the key is the copy that outlives this page.
      current = null;

      // Last writer wins, and the ordering that matters is a sign-out whose own
      // request comes back refused as expired: `signOut` clears in `finally`,
      // so 'signed-out' lands after 'expired' and the person who pressed the
      // button is told what they did rather than what the server said.
      ending = reason;
      storage.clear();
      notify();
    },
  };
}
